/**
 * @copyright
 * (c) 2026 netTrek GmbH & Co. KG
 *
 * C2PA end-entity certificate-profile enforcement (C2PA 2.4 §14.5.1.1), ported
 * from c2pa-rs `crypto/cose/certificate_profile.rs::check_certificate_profile` +
 * `check_end_entity_certificate_profile`. Run on the signing (leaf) certificate
 * INDEPENDENTLY of trust: a crypto-valid cert that violates the profile is
 * rejected with `signingCredential.invalid` → validation_state Invalid.
 *
 * Validity-at-signing-time (check 2 in the spec) is intentionally NOT duplicated
 * here — it is handled separately via `signingCredential.expired`.
 */

import type { CertInfo } from './parseCert'
import { parseStoreConfig } from './trust'
import { DEFAULT_TRUST_CONFIG } from './c2paTrustAnchors'
import { readTlv, readChildren, content, decodeOid } from './der'

/** Certificate signature algorithms allowed by the C2PA profile (§14.5.1.1). */
const ALLOWED_SIG_ALG_OIDS = new Set<string>([
    '1.2.840.113549.1.1.11', // sha256WithRSAEncryption
    '1.2.840.113549.1.1.12', // sha384WithRSAEncryption
    '1.2.840.113549.1.1.13', // sha512WithRSAEncryption
    '1.2.840.10045.4.3.2', // ecdsa-with-SHA256
    '1.2.840.10045.4.3.3', // ecdsa-with-SHA384
    '1.2.840.10045.4.3.4', // ecdsa-with-SHA512
    '1.2.840.113549.1.1.10', // RSASSA-PSS
    '1.3.101.112' // Ed25519
])
const RSASSA_PSS_OID = '1.2.840.113549.1.1.10'

/** Allowed EC named curves. */
const ALLOWED_EC_CURVES = new Set<string>([
    '1.2.840.10045.3.1.7', // prime256v1 (P-256)
    '1.3.132.0.34', // secp384r1 (P-384)
    '1.3.132.0.35' // secp521r1 (P-521)
])

const SHA_OIDS = new Set<string>([
    '2.16.840.1.101.3.4.2.1', // sha256
    '2.16.840.1.101.3.4.2.2', // sha384
    '2.16.840.1.101.3.4.2.3' // sha512
])

// Named EKU purpose OIDs for the timeStamping/OCSP exclusivity rule.
const EKU_OCSP = '1.3.6.1.5.5.7.3.9'
const EKU_TIMESTAMP = '1.3.6.1.5.5.7.3.8'
const EKU_ANY = '2.5.29.37.0'

/** The canonical C2PA allowed-EKU OID set (valid_eku_oids.cfg), trust-independent. */
const C2PA_EKU_OIDS: Set<string> = parseStoreConfig(DEFAULT_TRUST_CONFIG)

/** Validates RSASSA-PSS AlgorithmIdentifier parameters: hash == MGF1 hash, both a SHA-2. */
function pssParamsValid (params: Uint8Array | undefined): boolean {
    if (!params) return false
    try {
        const seq = readTlv(params, 0)
        const items = readChildren(params, seq.contentStart, seq.contentEnd)
        const hashWrap = items.find(t => t.tag === 0xa0) // [0] hashAlgorithm
        const mgfWrap = items.find(t => t.tag === 0xa1) // [1] maskGenAlgorithm
        if (!hashWrap || !mgfWrap) return false
        const hashAlg = readTlv(params, hashWrap.contentStart)
        const hashOid = decodeOid(content(params, readChildren(params, hashAlg.contentStart, hashAlg.contentEnd)[0]))
        // MGF1 AlgorithmIdentifier { mgf1 OID, params = hash AlgorithmIdentifier }
        const mgfAlg = readTlv(params, mgfWrap.contentStart)
        const mgfParts = readChildren(params, mgfAlg.contentStart, mgfAlg.contentEnd)
        const mgfHashAlg = readTlv(params, mgfParts[1].contentStart)
        const mgfHashOid = decodeOid(content(params, readChildren(params, mgfHashAlg.contentStart, mgfHashAlg.contentEnd)[0]))
        return hashOid === mgfHashOid && SHA_OIDS.has(hashOid)
    } catch {
        return false
    }
}

/**
 * Checks a signing (leaf) certificate against the C2PA end-entity profile.
 * Returns `{ valid: false, reason }` on the first violation, else `{ valid: true }`.
 */
export function checkCertificateProfile (cert: CertInfo): { valid: boolean, reason?: string } {
    // 1. Version must be v3.
    if (cert.version !== 3) return { valid: false, reason: 'version' }

    // 3. Certificate signature algorithm allow-list (+ RSASSA-PSS param check).
    if (!ALLOWED_SIG_ALG_OIDS.has(cert.signatureAlgOid)) return { valid: false, reason: 'sigAlg' }
    if (cert.signatureAlgOid === RSASSA_PSS_OID && !pssParamsValid(cert.sigAlgParams)) return { valid: false, reason: 'pssParams' }

    // 4. EC named-curve allow-list.
    if (cert.spkiAlgOid === '1.2.840.10045.2.1') {
        if (!cert.ecCurveOid || !ALLOWED_EC_CURVES.has(cert.ecCurveOid)) return { valid: false, reason: 'ecCurve' }
    }

    // 5. RSA / PSS modulus ≥ 2048 bits.
    if (cert.spkiAlgOid === '1.2.840.113549.1.1.1' || cert.spkiAlgOid === RSASSA_PSS_OID) {
        if ((cert.rsaModulusBits ?? 0) < 2048) return { valid: false, reason: 'rsaModulus' }
    }

    // 6. No self-signed (CA with issuer == subject).
    const issuerEqSubject = cert.issuerDer.length === cert.subjectDer.length &&
        cert.issuerDer.every((b, i) => b === cert.subjectDer[i])
    if (cert.isCa && issuerEqSubject) return { valid: false, reason: 'selfSigned' }

    // 7. No issuer/subject unique IDs.
    if (cert.hasUniqueId) return { valid: false, reason: 'uniqueId' }

    // 8. Extended Key Usage.
    if (cert.hasEku) {
        if (cert.eku.includes(EKU_ANY)) return { valid: false, reason: 'anyEku' }
        if (!cert.eku.some(e => C2PA_EKU_OIDS.has(e))) return { valid: false, reason: 'noAllowedEku' }
        // timeStamping/OCSP exclusivity (§14.5.1.1): a cert valid for either must be
        // valid for no other purpose (mirrors c2pa-rs's ocsp/timestamp EKU rule).
        const ocsp = cert.eku.includes(EKU_OCSP)
        const ts = cert.eku.includes(EKU_TIMESTAMP)
        const hasOtherPurpose = cert.eku.some(e => e !== EKU_OCSP && e !== EKU_TIMESTAMP && e !== EKU_ANY)
        if ((ocsp && ts) || ((ocsp !== ts) && hasOtherPurpose)) return { valid: false, reason: 'ekuExclusivity' }
    } else if (!cert.isCa) {
        // A non-CA leaf must carry the EKU extension.
        return { valid: false, reason: 'missingEku' }
    }

    // 8. Key Usage: digitalSignature / keyCertSign / nonRepudiation required; a non-CA
    //    leaf with keyCertSign is invalid.
    const ku = cert.keyUsage
    if (!ku) return { valid: false, reason: 'missingKeyUsage' }
    if (ku.keyCertSign && !cert.isCa) return { valid: false, reason: 'keyCertSign' }
    if (!(ku.digitalSignature || ku.keyCertSign || ku.nonRepudiation)) return { valid: false, reason: 'keyUsage' }

    // 8. AKI required; SKI required for CAs (not for non-CA leaves).
    if (!cert.hasAki) return { valid: false, reason: 'missingAki' }
    if (cert.isCa && !cert.hasSki) return { valid: false, reason: 'missingSki' }

    // 8. No unhandled critical extension.
    if (cert.unhandledCriticalExt) return { valid: false, reason: 'unhandledCritical' }

    // 9. End-entity signing cert must not be a CA.
    if (cert.isCa) return { valid: false, reason: 'leafIsCa' }

    return { valid: true }
}
